import type { openai } from '@ai-sdk/openai';
import { Button, Dialog, DialogContent, DialogTitle } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { ToolUIPart, UIDataTypes, UIMessagePart, UIToolInvocation, UITools } from 'ai';
import { Download, Maximize2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

function isToolPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolUIPart {
  return part.type.startsWith('tool-');
}

interface ImageGenerationToolProps {
  part: ToolUIPart;
  messageUuid: string;
}

type ImageGenerationToolInvocation = UIToolInvocation<ReturnType<typeof openai.tools.imageGeneration>>;

/**
 * Component for rendering image_generation tool output.
 */
export function ImageGenerationTool({ part, messageUuid }: ImageGenerationToolProps) {
  if (!isToolPart(part)) return null;

  // Handle output
  if (part.state === 'output-available') {
    return <ImageGenerationView invocation={part as ImageGenerationToolInvocation} messageUuid={messageUuid} />;
  }

  return null;
}

interface ImageGenerationViewProps {
  invocation: ImageGenerationToolInvocation;
  messageUuid: string;
}

function ImageGenerationView({ invocation, messageUuid }: ImageGenerationViewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const analytics = useAnalytics();

  const { imageSrc, filename } = useMemo(() => {
    const base64 = invocation.output?.result;
    if (!base64) {
      return { imageSrc: null as string | null, filename: null as string | null };
    }

    const src = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
    return { imageSrc: src, filename: 'generated-image.png' };
  }, [invocation.output?.result]);

  const handleDownload = useCallback(() => {
    if (!imageSrc || !filename) {
      return;
    }

    analytics.track({
      event: 'Image Generation Downloaded',
      properties: {
        toolCallId: invocation.toolCallId,
        messageUuid,
        filename,
      },
    });

    const link = document.createElement('a');
    link.href = imageSrc;
    link.download = filename;
    link.click();
  }, [analytics, filename, imageSrc, invocation.toolCallId, messageUuid]);

  const handleOpenFullscreen = useCallback(() => {
    analytics.track({
      event: 'Image Generation Viewed Fullscreen',
      properties: {
        toolCallId: invocation.toolCallId,
        messageUuid,
        filename,
      },
    });

    setIsOpen(true);
  }, [analytics, filename, invocation.toolCallId, messageUuid]);

  switch (invocation.state) {
    case 'input-available':
      return <div>Generating image...</div>;
    case 'output-available':
      if (!imageSrc) {
        return <div>Unable to load generated image.</div>;
      }

      return (
        <div className="flex flex-col items-start gap-2">
          <div className="bg-muted/20 inline-flex w-fit max-w-full flex-col items-start gap-2 rounded-md border p-3">
            <img
              src={imageSrc}
              alt="Generated by AI"
              className="max-h-48 w-auto max-w-full rounded-sm object-contain"
            />
            <div className="flex flex-row items-center gap-2">
              <Button
                variant="secondary"
                size="small"
                className="gap-1"
                onClick={handleOpenFullscreen}
                data-testid="image-generation-open"
              >
                <Maximize2 size={16} aria-hidden />
                View full screen
              </Button>
              <Button
                variant="secondary"
                size="small"
                className="gap-1"
                onClick={handleDownload}
                data-testid="image-generation-download"
              >
                <Download size={16} aria-hidden />
                Download
              </Button>
            </div>
          </div>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="bg-background inline-grid max-h-[90vh] w-auto max-w-[90vw] place-items-center p-0">
              <DialogTitle className="sr-only">Generated Image Fullscreen View</DialogTitle>
              <img src={imageSrc} alt="Generated by AI" className="block max-h-[90vh] max-w-[90vw] object-contain" />
            </DialogContent>
          </Dialog>
        </div>
      );
    default:
      return null;
  }
}
